import { useApolloClient } from '@apollo/client'
import { FetchViewQueryResultDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { checkVisualizeEnabled, getOrganizationIdFromPath, useMe } from '@salescore/client-common'
import { updateViewQueryResult, useAppDispatch } from '@salescore/client-redux'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQuery,
  ViewQueryResult,
} from '@salescore/core'
import { queueTimeWindowExecutorV2 } from '@salescore/frontend-common'
import { message } from 'antd'
import { t } from 'i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { useMustacheParameter } from '../../../hooks/useMustacheParameter'
import { kpiUserAppearanceAtom } from '../../navigation/atoms'
import { useSetRecordsImpl } from '../../records'
import { useMultiTablePivotQuery } from '../../viewConfig'
import { errorsAtom } from '../atoms'
import { useLoadingState } from '../hooks'
import { sendRefetchErrorToPosthog } from './useRefetchMutation'

export const useRefetchKpiPivotQueryResult = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
) => {
  const organizationId = getOrganizationIdFromPath()
  const mustacheParameter = useMustacheParameter()
  const query = useMultiTablePivotQuery(compiledConfig)
  const client = useApolloClient()
  const loading = useLoadingState()
  const me = useMe()
  const kpiUserAppearance = useRecoilValue(kpiUserAppearanceAtom)

  const setErrors = useSetRecoilState(errorsAtom)
  const setDataState = useSetRecordsImpl()
  const dispatch = useAppDispatch()

  const posthogProperties = {
    organizationId,
    organizationName: me.organization.name,
    viewId: view.id,
    viewType: view.type,
    viewName: view.name,
    userName: me.myUser.name,
    email: me.myUser.identity.email,
  }

  const applyViewQueryResult = (viewQueryResult: ViewQueryResult) => {
    dispatch(updateViewQueryResult({ viewId: view.id, incoming: viewQueryResult }))
    setDataState(viewQueryResult.result)

    const { error } = viewQueryResult
    if (error !== undefined && error !== '') {
      setErrors([error])
      sendRefetchErrorToPosthog({
        ...posthogProperties,
        error,
      })
      void message.error(t(`エラーが発生しました。エラー詳細ボタンから詳細をご確認ください。`))
      // return // sheetだとここでreturnするが、kpiの場合は複数KPI実行で一部のみがエラーになりうるため行わない
    } else {
      setErrors([])
    }
    if (viewQueryResult.warn !== undefined && viewQueryResult.warn !== '') {
      // i18n: バックエンドから以下の警告文が文字列として送られてくる場合がある。
      // `レコードが多すぎるため、全てを表示できません。`
      // `行が多すぎるため、全てを表示できません。「集計項目 - 行」を減らすと問題が解消する場合があります。`
      void message.warning(t(viewQueryResult.warn))
    }
    return viewQueryResult
  }

  const fetchViewQueryResult = async ({
    viewQuery,
    mustacheParameter,
    useCache,
  }: {
    viewQuery: ViewQuery
    mustacheParameter: Record<string, string | undefined>
    useCache?: boolean
  }) => {
    const { data: result } = await client.query({
      query: FetchViewQueryResultDocument,
      variables: {
        organizationId,
        viewQuery,
        mustacheParameter,
        viewId: view.id,
        unlimitedRowsEnabled: kpiUserAppearance.unlimitedRowsEnabled,
      },
      fetchPolicy: useCache === true ? 'cache-first' : 'network-only',
    })

    applyViewQueryResult(result.viewQueryResult)

    return result.viewQueryResult
  }

  const fetch = async ({
    viewQuery,
    mustacheParameter,
    useCache,
  }: {
    viewQuery: ViewQuery | undefined
    mustacheParameter: Record<string, string | undefined>
    useCache?: boolean
  }) => {
    if (!checkVisualizeEnabled(me.myUser)) {
      return
    }

    if (viewQuery === undefined) {
      return
    }

    if (query === undefined || query.queries.length === 0) {
      return
    }

    try {
      loading.setTrue()
      await fetchViewQueryResult({ viewQuery, mustacheParameter, useCache })
      loading.setFalse()
    } catch (error) {
      if (error instanceof Error) {
        setErrors([error.message])
        sendRefetchErrorToPosthog({
          ...posthogProperties,
          error: error.message,
        })
        void message.error(t(`エラーが発生しました。エラー詳細ボタンから詳細をご確認ください。`))
      }
    } finally {
      loading.setFalse()
    }
  }

  return {
    refetch(useCache?: boolean) {
      queueTimeWindowExecutorV2({
        key: `useRefetchKpiPivotQueryResult`,
        f: async () => {
          await fetch({ viewQuery: query, mustacheParameter, useCache })
        },
      })
    },
  }
}
